import React from 'react';

import { Card, Badge } from '@ergeon/core-components';
import { UserCircleIcon, ClockIcon } from '@ergeon/icons/hero/micro';
import { Money } from '@ergeon/erg-utils-js';

import { formatActivityDate } from '../../../utils/dateFormatters';
import { Order } from '../../../types';

type OrderCardProps = {
  order: Order;
  onClick?: () => void;
};

type BadgeFlavor = 'regular' | 'primary' | 'success' | 'danger' | 'warning';

const PRODUCT_BADGE_FLAVORS: Record<string, BadgeFlavor> = {
  fence: 'warning',
  driveway: 'regular',
  landscaping: 'success',
  online: 'danger',
};

const getProductBadgeFlavor = (productName: string): BadgeFlavor => {
  const nameLower = productName.toLowerCase();
  const matchedKey = Object.keys(PRODUCT_BADGE_FLAVORS).find((key) => nameLower.includes(key));
  return matchedKey ? PRODUCT_BADGE_FLAVORS[matchedKey] : 'regular';
};

const OrderCard: React.FC<OrderCardProps> = ({ order, onClick }) => {
  const hasQuoteValue = order.total_quote_value !== null && order.total_quote_value > 0;
  const lastActivity = formatActivityDate(order.last_activity_date);

  return (
    <Card
      className="flex flex-col gap-3 p-2" hasHover hoverType="outline" onClick={onClick}
      zIndex="2">
      {/* Upper section: Customer info + Product */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-start gap-2">
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <span className="text-gray-900 dark:text-white paragraph-3 font-medium line-clamp-2">
              {order.customer_name}
            </span>
            {order.address?.formatted_address && (
              <span className="text-gray-500 dark:text-gray-400 paragraph-3 truncate">
                {order.address.formatted_address}
              </span>
            )}
          </div>
          <span className="text-gray-900 dark:text-white paragraph-3 font-medium whitespace-nowrap">
            {hasQuoteValue ? Money.toString(order.total_quote_value!) : '$0'}
          </span>
        </div>
        <Badge flavor={getProductBadgeFlavor(order.product_name)} shape="square" taste="line" value={order.product_name} />
      </div>

      {/* Lower section: Sales rep + Date metadata */}
      <div className="flex flex-col gap-1">
        {order.sales_rep_name && (
          <div className="flex items-center gap-1.5">
            <UserCircleIcon className="size-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
            <span className="text-gray-500 dark:text-gray-400 paragraph-3 truncate">
              {order.sales_rep_name}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <ClockIcon className="size-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
          <span className="text-gray-500 dark:text-gray-400 paragraph-3">
            {lastActivity || '—'}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default OrderCard;
